import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, Circle, ArrowLeft, Loader2, FileText, Download, ListTodo } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CHECKLIST_ITEMS = [
  { key: 'atualizacao_cadastral', label: 'Atualização Cadastral' },
  { key: 'atualizacao_endereco', label: 'Atualização de Endereço' },
  { key: 'atualizacao_dados_pessoais', label: 'Atualização dos Dados Pessoais' },
  { key: 'vida_financeira', label: 'Vida Financeira' },
  { key: 'verificar_email', label: 'Verificar E-mail' },
  { key: 'consulta_seguro', label: 'Consulta de Seguro' },
  { key: 'antes', label: 'ANTES (Rating Inicial)' },
  { key: 'cadastro', label: 'CADASTRO (Estruturação)' },
  { key: 'depois', label: 'DEPOIS (Rating Final)' }
];

const RatingChecklist = () => {
  const { darkMode } = useSettings();
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchChecklist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/rating-checklist/${leadId}`);
      setChecklist(response.data.checklist);
    } catch (error) {
      console.error('Erro ao buscar checklist:', error);
    }
  }, [leadId]);

  const fetchLead = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${leadId}`);
      setLead(response.data);
    } catch (error) {
      console.error('Erro ao buscar lead:', error);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) {
      fetchChecklist();
      fetchLead();
    }
  }, [leadId, fetchChecklist, fetchLead]);

  const handleToggle = async (key) => {
    const newValue = checklist[key] === 1 ? 0 : 1;
    const updatedChecklist = { ...checklist, [key]: newValue };
    setChecklist(updatedChecklist);
    setSaving(true);

    try {
      await axios.put(`${API_URL}/rating-checklist/${leadId}/item`, {
        item: key,
        value: newValue
      });
    } catch (error) {
      console.error('Erro ao atualizar item:', error);
      setChecklist(checklist); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${leadId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rating_${lead?.tipo_pessoa}_${lead?.cpf_cnpj}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      alert('Erro ao baixar PDF');
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${leadId}/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rating_${lead?.tipo_pessoa}_${lead?.cpf_cnpj}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar Excel:', error);
      alert('Erro ao baixar Excel');
    }
  };

  const calculateProgress = () => {
    if (!checklist) return 0;
    const completed = CHECKLIST_ITEMS.filter(item => checklist[item.key] === 1).length;
    return Math.round((completed / CHECKLIST_ITEMS.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-gray-500" size={40} />
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Checklist não encontrado para este cliente</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clients')}
            className={`p-2 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            <ArrowLeft size={20} className={darkMode ? 'text-white' : 'text-gray-900'} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Checklist de Serviços
            </h1>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              {lead?.nome || 'Cliente'} - {lead?.tipo_pessoa === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saving && (
            <div className="flex items-center gap-2 text-blue-600">
              <Loader2 size={20} className="animate-spin" />
              <span>Salvando...</span>
            </div>
          )}
          <button
            onClick={() => navigate(`/clients/${leadId}/steps`)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ListTodo size={20} />
            Etapas do Serviço
          </button>
          <button
            onClick={downloadPDF}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            <FileText size={20} />
            PDF
          </button>
          <button
            onClick={downloadExcel}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download size={20} />
            Excel
          </button>
        </div>
      </div>

      {/* Progress Card */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Progresso do Serviço
          </h2>
          <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {calculateProgress()}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-green-600 h-4 rounded-full transition-all duration-300"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
        <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {CHECKLIST_ITEMS.filter(item => checklist[item.key] === 1).length} de {CHECKLIST_ITEMS.length} serviços concluídos
        </p>
      </div>

      {/* Checklist Items */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Serviços
        </h2>
        <div className="space-y-4">
          {CHECKLIST_ITEMS.map((item) => (
            <div
              key={item.key}
              className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                checklist[item.key] === 1
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : darkMode
                  ? 'border-gray-700 bg-gray-700/50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleToggle(item.key)}
                  className="focus:outline-none"
                >
                  {checklist[item.key] === 1 ? (
                    <CheckCircle size={28} className="text-green-600" />
                  ) : (
                    <Circle size={28} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                  )}
                </button>
                <span className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {item.label}
                </span>
              </div>
              {checklist[item.key] === 1 && (
                <span className="text-green-600 font-semibold text-sm">Concluído</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Client Info */}
      {lead && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Informações do Cliente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Nome</p>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{lead.nome}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {lead.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'}
              </p>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{lead.cpf_cnpj}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Telefone</p>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{lead.telefone}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>E-mail</p>
              <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{lead.email || '-'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingChecklist;
